import React, { useCallback } from 'react'
import PropTypes from 'prop-types'
import Tooltip from '../../ui/Tooltip'
import BellOnIcon from '../../ui/KpiCard/icons/BellOnIcon'
import ClockIcon from '../../ui/icons/ClockIcon'
import CircleCheckIcon from '../../ui/icons/CircleCheckIcon'
import ExclamationCircleIcon from '../../ui/icons/ExclamationCircleIcon'
import LinkButton from '../../ui/LinkButton'
import { categoryLabelForCount } from '../IssueStatuses/constants'
import { VISIBLE_STATUSES_PER_CARD, alertNoun } from './constants'
import StatusProgressRow from './StatusProgressRow'

export const DONE_TOOLTIP = 'Inclut les alertes non exploitables.'

const iconForCategory = category => {
  if (category === 'in_progress')
    return ClockIcon
  if (category === 'done')
    return CircleCheckIcon
  return BellOnIcon
}

const CARD_CLASS = 'tw:flex tw:flex-col tw:gap-4 tw:p-4 tw:bg-white tw:border tw:border-solid '
  + 'tw:border-gray-300 tw:rounded-lg tw:h-full'
const ICON_BOX_CLASS = 'tw:flex tw:items-center tw:justify-center tw:size-12 tw:rounded-lg '
  + 'tw:bg-gray-100 tw:shrink-0 tw:text-gray-600'
const RATE_TAG_CLASS = 'tw:inline-flex tw:items-center tw:text-xs tw:font-medium tw:rounded! '
  + 'tw:bg-green-100 tw:text-green-800 tw:px-2 tw:py-0.5'
const SHOW_ALL_CLASS = 'tw:inline-flex tw:items-center tw:gap-2 tw:self-start tw:text-xs '
  + 'tw:font-normal tw:no-underline! tw:text-gray-900! tw:hover:text-gray-900!'

const formatRate = (total, grandTotal) => {
  const percent = Math.round(total / grandTotal * 10000) / 100
  return String(percent).replace('.', ',')
}

const CategorySummaryCard = ({ category, label, total, statuses, grandTotal, onShowAll }) => {
  const Icon = iconForCategory(category)
  const displayLabel = total > 1 ? categoryLabelForCount(category, total) : label
  const resolutionRate = category === 'done' && grandTotal > 0 ? formatRate(total, grandTotal) : null
  const hasHiddenStatuses = statuses.length > VISIBLE_STATUSES_PER_CARD
  const showAll = () => onShowAll(category)
  const handleShowAll = useCallback(showAll, [onShowAll, category])
  return (
    <div aria-label={displayLabel} className={CARD_CLASS}>
      <div className="tw:flex tw:items-center tw:gap-3">
        <div className={ICON_BOX_CLASS}>
          <Icon className="tw:size-6" />
        </div>
        <div className="tw:flex tw:flex-col tw:gap-1 tw:min-w-0">
          <div className="tw:flex tw:items-center tw:gap-1 tw:text-sm tw:leading-tight tw:text-gray-500">
            <span>{displayLabel}</span>
            {category === 'done' ? (
              <Tooltip content={DONE_TOOLTIP}>
                <ExclamationCircleIcon className="tw:size-2.5" />
              </Tooltip>
            ) : null}
          </div>
          <div className="tw:flex tw:items-baseline tw:gap-1">
            <span className="tw:text-xl tw:font-bold tw:leading-tight tw:text-gray-900">{total}</span>
            <span className="tw:text-xs tw:leading-tight tw:text-gray-500">{alertNoun(total)}</span>
            {resolutionRate === null ? null : <span className={RATE_TAG_CLASS}>{`${resolutionRate} %`}</span>}
          </div>
        </div>
      </div>
      <div className="tw:flex tw:flex-col tw:gap-1" role="list">
        {statuses.slice(0, VISIBLE_STATUSES_PER_CARD).map(status => (
          <StatusProgressRow
            key={status.id}
            color={status.color}
            count={status.count}
            name={status.name}
            total={total}
          />
        ))}
      </div>
      {hasHiddenStatuses ? (
        <LinkButton className={SHOW_ALL_CLASS} onClick={handleShowAll}>
          <span className="tw:underline">{`Voir tous les ${statuses.length} statuts`}</span>
          <i aria-hidden="true" className="fas fa-chevron-right tw:text-base tw:text-gray-700" />
        </LinkButton>
      ) : null}
    </div>
  )
}

CategorySummaryCard.propTypes = {
  category: PropTypes.string,
  grandTotal: PropTypes.number,
  label: PropTypes.string,
  onShowAll: PropTypes.func,
  statuses: PropTypes.arrayOf(PropTypes.shape({
    color: PropTypes.string,
    count: PropTypes.number,
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    name: PropTypes.string
  })),
  total: PropTypes.number
}

CategorySummaryCard.defaultProps = {
  category: null,
  grandTotal: 0,
  label: '',
  onShowAll: () => null,
  statuses: [],
  total: 0
}

export default CategorySummaryCard
